import { storeToRefs } from 'pinia';
import axios from 'axios';
import { useMeStore } from '@/services/UserService/useMeStore';
import { getSupportArticleUrl } from '@/services/supportUrlService';
let _hubspotLoaded = false;
let _routeCheckInterval = null;

/**
 * Clear all HubSpot tracking cookies. HubSpot sets several cookies
 * (hubspotutk, __hstc, __hssc, __hssrc) that tie a browser to a
 * contact record. Without clearing all of them, HubSpot will
 * recognise the previous visitor even after re-identification.
 */
const _clearHubspotCookies = () => {
  const cookieNames = ['hubspotutk', '__hstc', '__hssc', '__hssrc'];
  const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
  const hostname = window.location.hostname;

  // Build a list of domain variants to try. HubSpot may set cookies
  // on the exact hostname, the parent domain, or with a leading dot.
  // For example, if the app runs on app.leanlaw.com, we need to clear
  // cookies set on: (no domain), app.leanlaw.com, .app.leanlaw.com,
  // leanlaw.com, and .leanlaw.com.
  const domains = [hostname, `.${hostname}`];
  const parts = hostname.split('.');
  for (let i = 1; i < parts.length - 1; i++) {
    const parent = parts.slice(i).join('.');
    domains.push(parent, `.${parent}`);
  }
  cookieNames.forEach(name => {
    // Clear with path=/ only (host-only cookie, no domain attribute)
    document.cookie = `${name}=; ${expiry}; path=/;`;
    // Clear for each domain variant
    domains.forEach(domain => {
      document.cookie = `${name}=; ${expiry}; path=/; domain=${domain};`;
    });
  });
};

/**
 * Remove HubSpot-related keys from localStorage on our origin.
 * The HubSpot tracking script and Conversations widget write
 * several keys (hs-*, __hs*, CONVERSATIONS_*) that can carry
 * visitor identity across page reloads.
 */
const _clearHubspotLocalStorage = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (/^hs[-_]/.test(key) || /^__hs/.test(key) || /^CONVERSATIONS/i.test(key) || /^hubspot/i.test(key))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (_) {
    // localStorage may be unavailable (private browsing, etc.)
  }
};

/**
 * Composable that provides support chat and URL helpers.
 *
 * Usage:
 *   const { getSupportUrl, openChat } = useSupportChat();
 */
export function useSupportChat() {
  const meStore = useMeStore();
  const {
    me
  } = storeToRefs(meStore);

  /**
   * Look up a support URL by article key.
   *
   * Keys are defined in SUPPORT_ARTICLES (supportUrlService.js).
   *
   * @param {string} articleKey e.g. 'conflictCheck', 'documentation'
   * @returns {string} Full URL guaranteed to exist
   */
  const getSupportUrl = articleKey => {
    return getSupportArticleUrl(articleKey);
  };

  // --------------- Chat SDK lifecycle ---------------

  /**
   * Fetch a visitor identification token from the backend.
   * The token is generated server-side using the HubSpot Conversations API
   * so visitors cannot spoof their identity.
   *
   * @returns {Promise<string>} The identification token, or empty string on failure
   */
  const _fetchVisitorToken = async () => {
    try {
      // Explicit short timeout: this call must never block the widget from
      // loading. When next-backend is degraded (e.g. slow Intuit/QuickBooks
      // calls starving its thread pool), the token endpoint can hang; without
      // a timeout the whole `_loadHubspot` await would stall and the widget
      // would never appear. Fail fast and fall back to loading without a
      // verified-visitor token (support context is still attached below).
      const {
        data
      } = await axios.post('/me/hubspot/token', null, {
        timeout: 5000
      });
      return data?.token || '';
    } catch (_) {
      // Widget still works without identity verification
      return '';
    }
  };

  /**
   * Push user identity to the HubSpot tracking code (_hsq).
   * This associates the visitor with a HubSpot contact record and
   * passes the LeanLaw CompanyId and CompanyName as custom properties
   * so support agents can see which company the user belongs to.
   */
  const _identifyHubspotUser = () => {
    const user = me.value;
    if (!user?.email) return;
    window._hsq = window._hsq || [];
    window._hsq.push(['identify', {
      email: user.email,
      firstname: user.first,
      lastname: user.last,
      leanlaw_company_id: user.company?.id || '',
      leanlaw_company_name: user.company?.name || ''
    }]);
  };

  /**
   * Push the current page path to HubSpot so agents can see what page
   * the user is on. In an SPA context, HubSpot doesn't detect route
   * changes automatically — we must call setPath + trackPageView.
   */
  const _trackCurrentPage = () => {
    window._hsq = window._hsq || [];
    window._hsq.push(['setPath', window.location.pathname + window.location.hash]);
    window._hsq.push(['trackPageView']);
  };

  /**
   * Start polling for SPA route changes so the HubSpot widget always
   * reports the current page URL to agents. We check the URL on an
   * interval because the app uses Angular UI-Router with hash-based
   * routing, which doesn't fire standard popstate events for every
   * navigation.
   */
  const _startRouteTracking = () => {
    if (_routeCheckInterval) return;
    let lastPath = window.location.pathname + window.location.hash;
    _routeCheckInterval = setInterval(() => {
      const currentPath = window.location.pathname + window.location.hash;
      if (currentPath !== lastPath) {
        lastPath = currentPath;
        _trackCurrentPage();
      }
    }, 1000);
  };

  /** Stop polling for route changes. */
  const _stopRouteTracking = () => {
    if (_routeCheckInterval) {
      clearInterval(_routeCheckInterval);
      _routeCheckInterval = null;
    }
  };

  /**
   * Dynamically load the HubSpot conversations script with visitor
   * identification. The Portal ID comes from the backend via
   * me.configuration.hubspotPortalId (sourced from Key Vault:
   * Hubspot:ChatPortalId).
   *
   * Before loading the script we:
   * 1. Fetch a server-side visitor identification token (anti-spoofing)
   * 2. Configure hsConversationsSettings so HubSpot associates the
   *    widget session with the authenticated user
   * 3. Push an identify call with email and custom properties
   * 4. Start tracking page changes for SPA context
   */
  const _loadHubspot = async () => {
    const portalId = me.value?.configuration?.hubspotPortalId;
    if (_hubspotLoaded || !portalId) return;

    // 0. Clear any leftover HubSpot cookies from a previous session.
    //    This is critical after login/logout: the tracking cookies
    //    survive page reloads and would link the NEW user to the
    //    OLD user's HubSpot contact record.
    _clearHubspotCookies();

    // 1. Fetch visitor identification token (server-generated, anti-spoofing)
    const identificationToken = await _fetchVisitorToken();

    // 2. Configure the VERIFIED-VISITOR feature before the script loads.
    //    SECURITY: Only pass identificationEmail when we have a valid
    //    server-generated token. Without the token, HubSpot trusts the
    //    email on the client's word — meaning anyone with a browser
    //    console could spoof another user's identity by setting
    //    identificationEmail to an arbitrary address. The server-side
    //    token (from /me/hubspot/token) proves the caller actually
    //    owns the email, so the two values must always be sent together.
    //    This stronger guarantee stays strictly gated on the token.
    if (me.value?.email && identificationToken) {
      window.hsConversationsSettings = {
        loadImmediately: true,
        identificationEmail: me.value.email,
        identificationToken
      };
    } else if (!identificationToken) {
      // No server token — commonly because next-backend is degraded by slow
      // Intuit/QuickBooks calls. Load the widget WITHOUT the verified-visitor
      // feature, but still attach company/user context below so support
      // tickets are attributable instead of anonymous.
      console.warn('[useSupportChat] HubSpot visitor token unavailable — loading widget without verified identity; support context still attached.');
    }

    // 3. Always push the identify call with the authenticated user's email and
    //    LeanLaw company custom properties, regardless of the token. This is
    //    what puts CompanyId / CompanyName on the HubSpot contact so support
    //    tickets aren't anonymous — the exact failure we are fixing (SC-46137):
    //    when the token endpoint was slow/unavailable, tickets arrived with no
    //    company or user data.
    //
    //    Doing this without the token is safe:
    //      - `me.value.email` comes from the server-authenticated /me response,
    //        NOT from client input, so it can't be spoofed here (unlike the
    //        verified-visitor `identificationEmail` above, which HubSpot itself
    //        would otherwise trust blindly and therefore must stay token-gated);
    //      - we clear HubSpot cookies on load (step 0) and on shutdown, so a
    //        previous user's identity can't leak into this session.
    _identifyHubspotUser();

    // 4. Load the HubSpot script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'hs-script-loader';
    script.async = true;
    script.defer = true;
    script.src = `https://js-na1.hs-scripts.com/${portalId}.js`;
    document.head.appendChild(script);
    _hubspotLoaded = true;

    // 5. Track the initial page and start watching for route changes
    _trackCurrentPage();
    _startRouteTracking();
  };
  const _shutdownHubspot = () => {
    if (!_hubspotLoaded) return;
    _stopRouteTracking();
    if (window.HubSpotConversations) {
      try {
        window.HubSpotConversations.clear({
          resetWidget: true
        });
      } catch (_) {
        /* noop */
      }
      try {
        window.HubSpotConversations.widget.remove();
      } catch (_) {
        /* noop */
      }
    }
    const el = document.getElementById('hs-script-loader');
    if (el) el.remove();
    const container = document.getElementById('hubspot-messages-iframe-container');
    if (container) container.remove();
    window.hsConversationsOnReady = [];
    delete window.hsConversationsSettings;
    _clearHubspotCookies();
    _clearHubspotLocalStorage();
    window._hsq = [];
    _hubspotLoaded = false;
  };

  /**
   * Initialise the HubSpot chat widget.
   * Call this once after the user is authenticated.
   */
  const loadChatWidget = async () => {
    if (me.value?.impersonation) {
      _shutdownHubspot();
      window.hsConversationsOnReady = [];
      return;
    }
    await _loadHubspot();
  };
  const shutdownChatWidget = () => {
    _shutdownHubspot();
  };

  /**
   * Open / show the chat widget (the "talk to us" action).
   * Uses the HubSpot callback queue so calls made before the async
   * script finishes loading are buffered and executed once the
   * Conversations API is ready.
   */
  const openChat = () => {
    if (me.value?.impersonation) return;
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.open();
    } else {
      window.hsConversationsOnReady = window.hsConversationsOnReady || [];
      window.hsConversationsOnReady.push(() => {
        window.HubSpotConversations.widget.open();
      });
    }
  };
  return {
    getSupportUrl,
    loadChatWidget,
    shutdownChatWidget,
    openChat
  };
}